/**
 * Reversal — Premium-Divergence Reversal, SENSEX + NIFTY paper/forward-test.
 * When spot falls into a support zone (pivot/PDL/CPR/swing/FVG) but the ATM PE gains
 * LESS than delta-expected (put writers absorbing the fall), buys CE on the reclaim
 * bar; mirror at resistance -> buys PE. SL = zone edge − 0.25 ATR, target = nearest
 * opposing zone (min 1.5R). Paper only — observe ~10 sessions.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api/zerodha";
const fmtTime = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";

function exportCSV(trades) {
  if (!trades.length) return;
  const h = "Session,Index,Side,OptType,Strike,Symbol,Qty,Zone,ZoneLevel,EntryPrem,EntryIdx,StopIdx,TargetIdx,RR,dSpot,Residual,ExitPrem,PnL,Result,ExitReason,EntryTime,ExitTime\n";
  const rows = trades.map(t => `${t.session},${t.index},${t.side},${t.opt_type},${t.strike},${t.symbol},${t.qty},"${t.zone_kind}",${t.zone_level},${t.entry_premium},${t.entry_idx},${t.stop_idx},${t.target_idx},${t.rr},${t.d_spot},${t.residual},${t.exit_premium ?? ""},${t.pnl || 0},${t.result || "OPEN"},${t.exit_reason || ""},${t.entry_time},${t.exit_time || ""}`).join("\n");
  const b = new Blob([h + rows], { type: "text/csv" }); const a = document.createElement("a");
  a.href = URL.createObjectURL(b); a.download = "reversal-trades.csv"; a.click();
}

const zoneChips = (zs, color) => (zs || []).map((z, i) => (
  <span key={i} style={{ background: "var(--panel3)", border: `1px solid ${color}44`, color, borderRadius: 4, padding: "1px 6px", marginRight: 4, fontSize: 10, whiteSpace: "nowrap" }}>
    {z.kind} {z.level}
  </span>
));

export default function Reversal() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const poll = async () => { try { const r = await axios.get(`${API}/reversal/status`, { timeout: 10000 }); if (r.data.success) setStatus(r.data); } catch {} };
  useEffect(() => { poll(); pollRef.current = setInterval(poll, 10000); return () => clearInterval(pollRef.current); }, []);
  const start = async () => { setLoading(true); try { await axios.post(`${API}/reversal/start`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const stop = async () => { setLoading(true); try { await axios.post(`${API}/reversal/stop`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const clear = async () => { if (!window.confirm("Clear closed reversal trades?")) return; try { await axios.post(`${API}/reversal/clear`); } catch {} setTimeout(poll, 500); };

  const trades = status?.trades || [];
  const open = trades.filter(t => t.status === "OPEN");
  const closed = trades.filter(t => t.status === "CLOSED");
  const sess = status?.session || {};

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>Reversal <span style={{ fontSize: 12, color: "#a78bfa" }}>(Premium-Divergence, SENSEX + NIFTY, paper)</span></h2>
          <p className="dashboard-subtitle">Spot falls into a support zone (pivot/PDL/CPR/swing/FVG) while the ATM PE gains less than delta-expected → put writers absorbing → buy CE on the reclaim bar. Mirror at resistance → buy PE. SL = zone − 0.25 ATR, target = nearest opposing zone (min 1.5R). Paper only.</p>
        </div>
        <div className="dashboard-actions">
          <button className={`btn ${status?.active ? "btn-danger" : "btn-primary"}`} onClick={status?.active ? stop : start} disabled={loading}>
            {status?.active ? "Stop Scanner" : "Start Scanner"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: status.active ? "var(--tint-pos)" : "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text)", flexWrap: "wrap", alignItems: "center" }}>
          <span>{status.active ? <span style={{ color: "#34d399" }}>SCANNING</span> : "STOPPED"}</span>
          <span>Checks: {status.scanCount}</span>
          {status.lastScanTime && <span>Last: {new Date(status.lastScanTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</span>}
          <span>Market: {status.marketOpen ? <span style={{ color: "#34d399" }}>OPEN</span> : <span style={{ color: "#f87171" }}>CLOSED</span>}</span>
          <span style={{ color: "var(--edge)" }}>|</span>
          <span>Sessions: <strong>{status.sessionsObserved}</strong></span>
          <span>Open: <strong>{status.openTrades}</strong></span>
          <span>Closed: <strong>{status.closed}</strong></span>
          <span>WR: <strong>{status.winRate}%</strong></span>
          <span>Booked: <strong className={status.totalPnl >= 0 ? "positive" : "negative"}>₹{status.totalPnl}</strong></span>
        </div>
      )}

      {(status?.indices || []).map(idx => {
        const s = sess[idx] || {};
        if (!s.date) return null;
        return (
          <div key={idx} style={{ padding: "6px 12px", fontSize: 11, color: "var(--grey-text2)", borderBottom: "1px solid var(--surface2)" }}>
            <strong style={{ color: "var(--grey-light)" }}>{idx}</strong> {s.date}: spot <strong>{s.spot}</strong> · ATR {s.atr}{s.atm ? <> · ATM <strong>{s.atm}</strong> exp {s.expiry}</> : null}
            <div style={{ marginTop: 3 }}>
              <span style={{ color: "var(--btn-border)", marginRight: 4 }}>S:</span>{zoneChips(s.zones?.supports, "#34d399")}
              <span style={{ color: "var(--btn-border)", margin: "0 4px" }}>R:</span>{zoneChips(s.zones?.resistances, "#f87171")}
            </div>
          </div>
        );
      })}

      {open.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#f59e0b" }}>Open Positions ({open.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Index</th><th>Type</th><th>Strike</th><th>Qty</th><th>Zone</th><th>Entry ₹</th><th>Idx Entry</th><th>Stop</th><th>Target</th><th>RR</th><th>Resid</th><th>Entry Time</th></tr></thead>
              <tbody>
                {open.map(t => (
                  <tr key={t.sig_key} className="trade-open-row">
                    <td>{t.index}</td>
                    <td><span className={`signal-badge ${t.opt_type === "CE" ? "badge-long" : "badge-short"}`}>{t.opt_type}</span></td>
                    <td>{t.strike}</td><td>{t.qty}</td>
                    <td style={{ fontSize: 10 }}>{t.zone_kind} @ {t.zone_level}</td>
                    <td><strong>{t.entry_premium}</strong></td>
                    <td>{t.entry_idx}</td><td className="sl">{t.stop_idx}</td><td style={{ color: "#34d399" }}>{t.target_idx}</td>
                    <td>{t.rr}R</td><td style={{ color: "#a78bfa" }}>{t.residual}</td>
                    <td style={{ fontSize: 10 }}>{fmtTime(t.entry_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13, color: "var(--grey-text)" }}>Closed Trades ({closed.length})</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-export" onClick={() => exportCSV(trades)}>Export CSV</button>
            {closed.length > 0 && <button className="btn btn-sm btn-danger" onClick={clear}>Clear</button>}
          </div>
        </div>
        {closed.length > 0 && (
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Session</th><th>Index</th><th>Type</th><th>Strike</th><th>Zone</th><th>Entry ₹</th><th>Exit ₹</th><th>P&L</th><th>Result</th><th>Reason</th><th>RR</th><th>Exit Time</th></tr></thead>
              <tbody>
                {closed.map(t => (
                  <tr key={t.sig_key} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                    <td style={{ fontSize: 10 }}>{t.session}</td>
                    <td>{t.index}</td>
                    <td><strong style={{ color: t.opt_type === "CE" ? "#34d399" : "#f87171" }}>{t.opt_type}</strong></td>
                    <td>{t.strike}</td>
                    <td style={{ fontSize: 10 }}>{t.zone_kind} @ {t.zone_level}</td>
                    <td>{t.entry_premium}</td><td>{t.exit_premium}</td>
                    <td className={t.pnl >= 0 ? "positive" : "negative"}>{t.pnl > 0 ? "+" : ""}₹{t.pnl}</td>
                    <td><span style={{ color: t.result === "WIN" ? "#34d399" : "#f87171", fontWeight: 700 }}>{t.result || "?"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.exit_reason}</td><td>{t.rr}R</td>
                    <td style={{ fontSize: 10 }}>{fmtTime(t.exit_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!status?.active && closed.length === 0 && open.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click <strong>Start Scanner</strong> to forward-test the Premium-Divergence Reversal on SENSEX + NIFTY.</p>
          <p style={{ fontSize: 12 }}>Scans 5-min bars during market hours. Enters only when a zone touch, a reclaim close AND put/call-writing divergence line up, with at least 1.5R to the nearest opposing zone. Let it run ~10 sessions.</p>
        </div>
      )}
    </div>
  );
}
